import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { isRouteAccessible, UserRole } from '../../utils/rbac';
import toast from 'react-hot-toast';

interface ProtectedRouteProps {
    children: React.ReactNode;
    requiredRole?: UserRole;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
    children,
    requiredRole
}) => {
    const { token, user } = useSelector((state: RootState) => state.auth);
    const location = useLocation();

    // Use Redux auth state with fallback to localStorage
    const adminToken = localStorage.getItem('diasporawise_admin_token') || localStorage.getItem('token');
    const adminUserStr = localStorage.getItem('diasporawise_admin_user') || localStorage.getItem('user');
    const userToken = localStorage.getItem('diasporawise_user_token');
    const userDataStr = localStorage.getItem('diasporawise_user_data');

    const effectiveToken = token || adminToken || userToken;
    let effectiveUser = user;
    if (!effectiveUser) {
        try {
            if (adminUserStr) effectiveUser = JSON.parse(adminUserStr);
            else if (userDataStr) effectiveUser = JSON.parse(userDataStr);
            else if (adminToken) effectiveUser = { role: 'admin', email: 'admin@diasporawise.com' };
        } catch (e) {
            effectiveUser = { role: 'admin', email: 'admin@diasporawise.com' };
        }
    }

    // Check if user is authenticated
    if (!effectiveToken) {
        return <Navigate to="/" replace />;
    }

    const currentUser = effectiveUser || { role: 'admin' };

    // Check role-based access
    if (requiredRole && currentUser.role !== requiredRole) {
        toast.error('You do not have permission to access this page');
        if (currentUser.role === 'DEALER') {
            return <Navigate to="/dealer/dashboard" replace />;
        } else if (currentUser.role === 'USER') {
            return <Navigate to="/user/profile" replace />;
        }
        return <Navigate to="/admin" replace />;
    }

    // For dealer routes, skip RBAC check (dealers have their own route structure)
    if (currentUser.role === 'DEALER' && location.pathname.startsWith('/dealer')) {
        return <>{children}</>;
    }

    // Check route-specific permissions for admin routes
    if (currentUser.role === 'admin' || currentUser.role === 'manager' || currentUser.role === 'exclusive') {
        const hasAccess = isRouteAccessible(currentUser.role as UserRole, location.pathname);
        console.log('🛡️ ProtectedRoute Permission check:', {
            userRole: currentUser.role,
            pathname: location.pathname,
            hasAccess,
            userEmail: currentUser.email
        });
        
        if (!hasAccess) {
            console.log('❌ Access denied for:', { userRole: currentUser.role, pathname: location.pathname });
            toast.error('You do not have permission to access this page');
            return <Navigate to="/admin" replace />;
        }
        
        console.log('✅ Access granted for:', { userRole: currentUser.role, pathname: location.pathname });
    }

    return <>{children}</>;
};

export default ProtectedRoute; 